// src/components/common/Sidebar.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Dashboard,
  Restaurant,
  Category,
  Fastfood,
  Add,
  Receipt,
  TableRestaurant,
  Schedule as ScheduleIcon,
  KeyboardDoubleArrowRight,
  KeyboardDoubleArrowLeft,
  Logout,
  BarChart,
  PointOfSale,
  Warehouse,
  MenuBook,
  People,
  RoomService,
  AccountBalance,
  TrendingUp,
  AccessTime,
  Assessment,
  Settings as SettingsIcon,
  NotificationsNone,
  LocalShipping,
  Person,
  School,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ROUTES, QUERY_KEYS, getImageUrl } from '../../constants';
import { UserRole, Restaurant as RestaurantType } from '../../types';
import { restaurantApi } from '../../api';
import { materialApi } from '../../api/material.api';
import { orderApi } from '../../api/order.api';
import { OrderStatus } from '../../types';
import SidebarNavItem from './SidebarNavItem';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onCollapse?: (collapsed: boolean) => void;
}

const SIDEBAR_WIDTH_EXPANDED = 280;
const SIDEBAR_WIDTH_COLLAPSED = 72;

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, direction, isRTL } = useLanguage();
  const [collapsed, setCollapsed] = useState(false);

  // Fetch restaurant profile for logo (only for restaurant owners)
  const { data: restaurant } = useQuery({
    queryKey: [QUERY_KEYS.RESTAURANT_PROFILE],
    queryFn: async () => {
      const response = await restaurantApi.getMyProfile();
      return response.data.data as RestaurantType;
    },
    enabled: user?.role === UserRole.RESTAURANT_OWNER || user?.role === UserRole.CASHIER || user?.role === UserRole.WAITER || user?.role === UserRole.ACCOUNTANT || user?.role === UserRole.DELIVERY_CASHIER || user?.role === UserRole.INVENTORY_MANAGER,
  });

  // Get logo URL
  const getLogoUrl = () => {
    if (restaurant?.logo) {
      return getImageUrl(restaurant.logo);
    }
    return null;
  };

  const logoUrl = getLogoUrl();
  const displayName = restaurant?.name || user?.username || '';

  // Fetch anomalies for notifications badge
  const { data: anomaliesResponse } = useQuery({
    queryKey: [QUERY_KEYS.MATERIALS, 'anomalies'],
    queryFn: () => materialApi.getAnomalies(),
    enabled: user?.role === UserRole.RESTAURANT_OWNER || user?.role === UserRole.INVENTORY_MANAGER,
    refetchInterval: 60000, // Refresh every minute
  });

  const notificationCount = anomaliesResponse?.data?.data?.length || 0;

  // Fetch pending orders for orders badge
  const { data: pendingOrdersResponse } = useQuery({
    queryKey: ['orders', 'pendingCount'],
    queryFn: () => orderApi.getRestaurantOrders({ status: OrderStatus.PENDING, limit: 1 }),
    enabled: user?.role === UserRole.RESTAURANT_OWNER || user?.role === UserRole.CASHIER,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const pendingOrdersCount = pendingOrdersResponse?.data?.pagination?.total || 0;

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const isActive = (path: string) => {
    if (path === ROUTES.ADMIN_RESTAURANTS_ALL_STATS) {
      return location.pathname === path || (location.pathname.startsWith('/admin/restaurants/') && location.pathname.endsWith('/stats'));
    }
    return location.pathname === path;
  };

  const toggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    onCollapse?.(newCollapsed);
  };

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
    onClose();
  };

  const adminMenuItems = [
    { text: t('sidebar.adminDashboard'), icon: Dashboard, path: ROUTES.ADMIN_DASHBOARD, color: '#6366f1' },
    { text: t('sidebar.restaurants'), icon: Restaurant, path: ROUTES.ADMIN_RESTAURANTS, color: '#6523d1' },
    { text: 'إحصائيات المطاعم', icon: BarChart, path: ROUTES.ADMIN_RESTAURANTS_ALL_STATS, color: '#8B5CF6' },
  ];

  const restaurantMenuItems = [
    { text: t('sidebar.dashboard'), icon: Dashboard, path: ROUTES.RESTAURANT_DASHBOARD, color: '#6366f1', tourTarget: 'sidebar-dashboard' },
    { text: t('nav.notifications', { defaultValue: 'الإشعارات' }), icon: NotificationsNone, path: ROUTES.RESTAURANT_NOTIFICATIONS, color: '#ef4444', badgeCount: notificationCount },
    { text: t('sidebar.orders'), icon: Receipt, path: ROUTES.RESTAURANT_ORDERS, color: '#6366f1' },
    { text: t('sidebar.cashier', { defaultValue: 'كاشير' }), icon: PointOfSale, path: ROUTES.RESTAURANT_CASHIER, color: '#6366f1', tourTarget: 'sidebar-pos' },
    { text: t('sidebar.tables'), icon: TableRestaurant, path: ROUTES.RESTAURANT_TABLES, color: '#6366f1' },
    { text: t('sidebar.categories'), icon: Category, path: ROUTES.RESTAURANT_CATEGORIES, color: '#6366f1', tourTarget: 'sidebar-menu' },
    { text: t('sidebar.dishes'), icon: Fastfood, path: ROUTES.RESTAURANT_DISHES, color: '#6366f1' },
    { text: t('sidebar.inventory'), icon: Warehouse, path: ROUTES.RESTAURANT_INVENTORY, color: '#6366f1', tourTarget: 'sidebar-inventory' },
    { text: t('sidebar.templates'), icon: MenuBook, path: ROUTES.RESTAURANT_TEMPLATES, color: '#6366f1' },
    { text: t('sidebar.scheduling'), icon: ScheduleIcon, path: ROUTES.RESTAURANT_SCHEDULING, color: '#6366f1' },
    { text: 'إدارة الموظفين', icon: People, path: ROUTES.RESTAURANT_STAFF, color: '#6366f1', tourTarget: 'sidebar-staff' },
    { text: 'صندوق النقد', icon: PointOfSale, path: ROUTES.RESTAURANT_CASH_REGISTER, color: '#6366f1' },
    { text: 'شركات التوصيل', icon: LocalShipping, path: ROUTES.RESTAURANT_DELIVERY_COMPANIES, color: '#f97316', tourTarget: 'sidebar-delivery' },
    { text: 'التقارير', icon: Assessment, path: ROUTES.RESTAURANT_REPORTS, color: '#6366f1', tourTarget: 'sidebar-reports' },
    { text: t('sidebar.settings'), icon: SettingsIcon, path: ROUTES.RESTAURANT_SETTINGS, color: '#6366f1', tourTarget: 'sidebar-settings' },
  ];

  // Cashier has access to: POS, Orders, Scheduling, Templates, Tables, Profile
  const cashierMenuItems = [
    { text: t('sidebar.cashier', { defaultValue: 'كاشير' }), icon: PointOfSale, path: ROUTES.RESTAURANT_CASHIER, color: '#6366f1', tourTarget: 'sidebar-pos' },
    { text: t('sidebar.orders'), icon: Receipt, path: ROUTES.RESTAURANT_ORDERS, color: '#6366f1', badgeCount: pendingOrdersCount, tourTarget: 'sidebar-orders' },
    { text: t('sidebar.scheduling'), icon: ScheduleIcon, path: ROUTES.RESTAURANT_SCHEDULING, color: '#6366f1', tourTarget: 'sidebar-scheduling' },
    { text: t('sidebar.templates'), icon: MenuBook, path: ROUTES.RESTAURANT_TEMPLATES, color: '#6366f1', tourTarget: 'sidebar-templates' },
    { text: t('sidebar.tables'), icon: TableRestaurant, path: ROUTES.RESTAURANT_TABLES, color: '#6366f1', tourTarget: 'sidebar-tables' },
    { text: t('sidebar.settings'), icon: SettingsIcon, path: ROUTES.RESTAURANT_SETTINGS, color: '#6366f1', tourTarget: 'sidebar-settings' },
  ];

  // Waiter has access to: POS, Profile
  const waiterMenuItems = [
    { text: t('sidebar.cashier', { defaultValue: 'كاشير' }), icon: PointOfSale, path: ROUTES.RESTAURANT_CASHIER, color: '#6366f1' },
    { text: t('sidebar.settings'), icon: SettingsIcon, path: ROUTES.RESTAURANT_SETTINGS, color: '#6366f1', tourTarget: 'sidebar-settings' },
  ];

  // Accountant: financial overview + read-only operational data + Profile
  const accountantMenuItems = [
    { text: 'لوحة المحاسب', icon: Assessment, path: ROUTES.RESTAURANT_ACCOUNTANT, color: '#6366f1', tourTarget: 'sidebar-accountant' },
    { text: t('sidebar.orders'), icon: Receipt, path: ROUTES.RESTAURANT_ORDERS, color: '#6366f1', badgeCount: pendingOrdersCount, tourTarget: 'sidebar-orders' },
    { text: 'المصاريف', icon: AccountBalance, path: ROUTES.RESTAURANT_EXPENSES, color: '#6366f1', tourTarget: 'sidebar-expenses' },
    { text: t('sidebar.inventory'), icon: Warehouse, path: ROUTES.RESTAURANT_INVENTORY, color: '#6366f1', tourTarget: 'sidebar-inventory' },
    { text: t('sidebar.templates'), icon: MenuBook, path: ROUTES.RESTAURANT_TEMPLATES, color: '#6366f1', tourTarget: 'sidebar-templates' },
    { text: 'التقارير', icon: Assessment, path: ROUTES.RESTAURANT_REPORTS, color: '#6366f1', tourTarget: 'sidebar-reports' },
    { text: 'صندوق النقد', icon: PointOfSale, path: ROUTES.RESTAURANT_CASH_REGISTER, color: '#6366f1', tourTarget: 'sidebar-cash-register' },
    { text: 'شركات التوصيل', icon: LocalShipping, path: ROUTES.RESTAURANT_DELIVERY_COMPANIES, color: '#f97316', tourTarget: 'sidebar-delivery-companies' },
    { text: t('sidebar.settings'), icon: SettingsIcon, path: ROUTES.RESTAURANT_SETTINGS, color: '#6366f1', tourTarget: 'sidebar-settings' },
  ];

  // Delivery Cashier: delivery cashier (الكاشير), delivery orders (الطلبات) pages, and Profile
  const deliveryCashierMenuItems = [
    { text: 'الكاشير', icon: PointOfSale, path: ROUTES.RESTAURANT_CASHIER, color: '#f97316', tourTarget: 'sidebar-pos' },
    { text: 'الطلبات', icon: Receipt, path: ROUTES.RESTAURANT_DELIVERY_ORDERS, color: '#f97316', tourTarget: 'sidebar-delivery-orders' },
    { text: t('sidebar.settings'), icon: SettingsIcon, path: ROUTES.RESTAURANT_SETTINGS, color: '#6366f1', tourTarget: 'sidebar-settings' },
  ];

  // Inventory Manager: Inventory, Preparation Templates, Profile, Notifications
  const inventoryManagerMenuItems = [
    { text: t('sidebar.inventory'), icon: Warehouse, path: ROUTES.RESTAURANT_INVENTORY, color: '#6366f1', tourTarget: 'sidebar-inventory' },
    { text: t('sidebar.templates'), icon: MenuBook, path: ROUTES.RESTAURANT_TEMPLATES, color: '#6366f1', tourTarget: 'sidebar-templates' },
    { text: t('nav.notifications', { defaultValue: 'الإشعارات' }), icon: NotificationsNone, path: ROUTES.RESTAURANT_NOTIFICATIONS, color: '#ef4444', badgeCount: notificationCount, tourTarget: 'sidebar-notifications' },
    { text: t('sidebar.settings'), icon: SettingsIcon, path: ROUTES.RESTAURANT_SETTINGS, color: '#6366f1', tourTarget: 'sidebar-settings' },
  ];

  let menuItems;
  switch (user?.role) {
    case UserRole.ADMIN:
      menuItems = adminMenuItems;
      break;
    case UserRole.CASHIER:
      menuItems = cashierMenuItems;
      break;
    case UserRole.WAITER:
      menuItems = waiterMenuItems;
      break;
    case UserRole.ACCOUNTANT:
      menuItems = accountantMenuItems;
      break;
    case UserRole.DELIVERY_CASHIER:
      menuItems = deliveryCashierMenuItems;
      break;
    case UserRole.INVENTORY_MANAGER:
      menuItems = inventoryManagerMenuItems;
      break;
    default:
      menuItems = restaurantMenuItems;
  }

  const drawerContent = (
    <Box
      sx={{
        width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
        height: '100%',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        fontFamily: 'Cairo, Arabic, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.02) 0%, transparent 100%)',
          pointerEvents: 'none',
        },
      }}
      dir={direction}
    >
      {/* Logo/Brand Area */}
      <Box
        sx={{
          py: 0,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: 64,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Restaurant Logo or Fallback Avatar */}
            {logoUrl ? (
              <Avatar
                src={logoUrl}
                alt={displayName}
                sx={{
                  width: 44,
                  height: 44,
                  border: '2px solid rgba(99, 102, 241, 0.2)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1 0%, #6523d1 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 800,
                  fontSize: '1.125rem',
                }}
              >
                {displayName.charAt(0).toUpperCase()}
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.938rem',
                  fontWeight: 700,
                  color: 'rgb(30, 41, 59)',
                  fontFamily: 'Cairo, Arabic, sans-serif',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'rgb(100, 116, 139)',
                  fontFamily: 'Cairo, Arabic, sans-serif',
                  fontWeight: 500,
                }}
              >
                {user?.role === UserRole.ADMIN
                  ? t('sidebar.systemAdmin')
                  : user?.role === UserRole.CASHIER
                  ? 'كاشير'
                  : user?.role === UserRole.WAITER
                  ? 'نادل'
                  : user?.role === UserRole.ACCOUNTANT
                  ? 'محاسب'
                  : user?.role === UserRole.DELIVERY_CASHIER
                  ? 'كاشير شركات التوصيل'
                  : user?.role === UserRole.INVENTORY_MANAGER
                  ? 'مدير مخزن'
                  : t('sidebar.restaurant')}
              </Typography>
            </Box>
          </Box>
        )}

        <IconButton
          onClick={toggleCollapse}
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'rgba(99, 102, 241, 0.08)',
            color: '#6366f1',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'rgba(99, 102, 241, 0.15)',
              transform: 'scale(1.05)',
            },
          }}
        >
          {isRTL ? (
            collapsed ? <KeyboardDoubleArrowLeft sx={{ fontSize: 20 }} /> : <KeyboardDoubleArrowRight sx={{ fontSize: 20 }} />
          ) : (
            collapsed ? <KeyboardDoubleArrowRight sx={{ fontSize: 20 }} /> : <KeyboardDoubleArrowLeft sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(209, 213, 219, 0.3)' }} />

      {/* Menu Items */}
      <Box
        sx={{
          flex: 1,
          py: 2,
          px: collapsed ? 1.5 : 2,
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          zIndex: 1,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(148, 163, 184, 0.3)',
            borderRadius: '10px',
            '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.5)' },
          },
        }}
      >
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => (
            <SidebarNavItem
              key={item.path}
              text={item.text}
              icon={item.icon}
              path={item.path}
              color={item.color}
              active={isActive(item.path)}
              collapsed={collapsed}
              isRTL={isRTL}
              onNavigate={handleNavigate}
              badgeCount={(item as any).badgeCount}
              tourTarget={(item as any).tourTarget}
            />
          ))}
        </List>
      </Box>

      {/* Logout Button */}
      <Divider sx={{ borderColor: 'rgba(209, 213, 219, 0.3)' }} />
      <Box sx={{ p: collapsed ? 1.5 : 2, position: 'relative', zIndex: 1 }}>
        <Tooltip
          title={collapsed ? t('sidebar.logout') : ''}
          placement={isRTL ? 'left' : 'right'}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'white',
                color: '#ef4444',
                fontSize: '0.813rem',
                fontFamily: 'Cairo, Arabic, sans-serif',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              },
            },
            arrow: {
              sx: {
                color: 'white',
                '&::before': { border: '1px solid rgba(239, 68, 68, 0.3)' },
              },
            },
          }}
        >
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2.5,
              px: collapsed ? 1.5 : 2,
              py: 1.5,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: collapsed ? 'center' : 'flex-start',
              // bgcolor: 'rgba(239, 68, 68, 0.05)',
              // border: '1px solid rgba(239, 68, 68, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                borderColor: 'rgba(239, 68, 68, 0.2)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', marginInlineEnd: collapsed ? 0 : 2, color: '#ef4444' }}>
              <Logout sx={{ fontSize: 22 }} />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={t('sidebar.logout')}
                sx={{
                  m: 0,
                  textAlign: 'right',
                  '& .MuiListItemText-primary': {
                    fontFamily: 'Cairo, Arabic, sans-serif',
                    fontSize: '0.938rem',
                    fontWeight: 600,
                    color: '#ef4444',
                  },
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        anchor={isRTL ? 'right' : 'left'}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiDrawer-paper': {
            width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
            boxSizing: 'border-box',
            position: 'fixed',
            top: 0,
            [isRTL ? 'right' : 'left']: 0,
            height: '100vh',
            border: 'none',
            [isRTL ? 'borderLeft' : 'borderRight']: '1px solid rgba(209, 213, 219, 0.2)',
            boxShadow: isRTL ? '-4px 0 12px rgba(0, 0, 0, 0.04)' : '4px 0 12px rgba(0, 0, 0, 0.04)',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 1200,
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor={isRTL ? 'right' : 'left'}
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIDEBAR_WIDTH_EXPANDED,
            border: 'none',
            zIndex: 1300,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
